/**
 * Calendar booking step — RN counterpart of the web widget's
 * `components/steps/calendar-step.tsx`.
 *
 * The selection model is unchanged: pick a day, then a slot inside that day,
 * then confirm. Grouping, ordering and labelling all come from
 * `component-types` (`groupSlotsByDate` / `formatSlotTime`), so both platforms
 * show the same slots in the same words for the same payload.
 *
 * Two surfaces could not cross unchanged: the 3-column CSS grid, which becomes
 * fixed-width flex cells, and the framer-motion reveal, which becomes a fade
 * (RN cannot animate to an intrinsic height without measuring it first).
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FC } from "react";
import { Animated, Easing, StyleSheet, Text, View } from "react-native";
import { Calendar, Clock, LoaderCircle } from "lucide-react-native";
import type { ComponentTheme } from "@ringg/core";

import { formatSlotTime, groupSlotsByDate, type CalendarBookingPayload, type CalendarSlot } from "../../lib/component-types";
import { GradientFill, solidColor } from "../../lib/gradient";
import { getButtonRadius, mergeTheme } from "../../lib/theme-utils";
import { resolveFontFamily, useWidgetTheme } from "../../lib/widget-theme";
import { Touchable } from "../ui/pressable";

interface CalendarStepProps {
  payload: CalendarBookingPayload;
  isProcessing?: boolean;
  onComplete: (slotId: string, slotDatetime: string) => void;
}

/** `animate-spin` — one linear turn per second, the CSS default. */
const SPIN_DURATION_MS = 1000;

/** Long enough to read as a reveal, short enough not to delay a second tap. */
const SLOT_FADE_MS = 200;

/** Gutter between time slots (web's `gap-1.5`), split across the two cells. */
const SLOT_GUTTER = 6;

/**
 * Half the gap between neighbouring chips. Anything larger and the expanded
 * touch areas overlap, which hands taps near the seam to whichever chip
 * happens to be first in the tree.
 */
const DATE_HIT_SLOP = 4;
const SLOT_HIT_SLOP = SLOT_GUTTER / 2;

/**
 * Web resolves the component theme from a context that `interactive-flow`
 * installs. RN resolves it at the leaf: the inputs are the same — widget
 * colours as the base, the payload's own `theme` on top — and a step rendered
 * on its own, outside a flow, still themes correctly.
 */
const useStepTheme = (componentTheme?: ComponentTheme): Required<ComponentTheme> => {
  const widgetTheme = useWidgetTheme();

  return useMemo(
    () =>
      mergeTheme({
        primaryColor: widgetTheme.primaryColor,
        primaryTextColor: widgetTheme.primaryTextColor,
        // Cards read as bordered surfaces sitting ON the panel, so they take
        // the widget background rather than the (greyer) surface colour.
        backgroundColor: widgetTheme.backgroundColor,
        surfaceColor: widgetTheme.surfaceColor,
        textColor: widgetTheme.textColor,
        mutedTextColor: widgetTheme.mutedTextColor,
        borderColor: widgetTheme.borderColor,
        errorColor: widgetTheme.errorColor,
        successColor: widgetTheme.successColor,
        buttonStyle: widgetTheme.buttonStyle,
        // Web inherits the family from the flow container; RN has no cascade,
        // so it travels through the theme and is applied per <Text>.
        fontFamily: widgetTheme.fontFamily,
        ...componentTheme,
      }),
    [componentTheme, widgetTheme],
  );
};

/** `LoaderCircle` is lucide's current name for the web source's `Loader2`. */
const Spinner: FC<{ color: string }> = ({ color }) => {
  const turn = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(Animated.timing(turn, { toValue: 1, duration: SPIN_DURATION_MS, easing: Easing.linear, useNativeDriver: true }));
    loop.start();
    return () => loop.stop();
  }, [turn]);

  return (
    <Animated.View style={{ transform: [{ rotate: turn.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] }) }] }}>
      <LoaderCircle size={16} color={color} />
    </Animated.View>
  );
};

const CalendarStep: FC<CalendarStepProps> = ({ payload, isProcessing, onComplete }) => {
  const theme = useStepTheme(payload.theme);
  const buttonRadius = getButtonRadius(theme.buttonStyle);
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const { data } = payload;
  const groupedSlots = useMemo(() => groupSlotsByDate(data.available_slots), [data.available_slots]);

  const [selectedDate, setSelectedDate] = useState<string>(groupedSlots[0]?.date || "");
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);

  const selectedDateGroup = groupedSlots.find((g) => g.date === selectedDate);

  // Web animates opacity AND height from 0 on every date change; only the
  // opacity half survives, so the slots take their space immediately.
  const slotFade = useRef(new Animated.Value(0)).current;

  // Layout effect, not passive: resetting the value after paint lets the new
  // slots show at full opacity for one frame before fading in.
  useLayoutEffect(() => {
    slotFade.setValue(0);
    const fade = Animated.timing(slotFade, { toValue: 1, duration: SLOT_FADE_MS, useNativeDriver: true });
    fade.start();
    return () => fade.stop();
  }, [selectedDate, slotFade]);

  const handleConfirm = () => {
    if (selectedSlot) {
      onComplete(selectedSlot.id, selectedSlot.datetime);
    }
  };

  const isConfirmDisabled = !selectedSlot || isProcessing;
  const confirmTextColor = isConfirmDisabled ? theme.mutedTextColor : theme.primaryTextColor;

  return (
    <View>
      <View style={styles.header}>
        <Calendar size={16} color={solidColor(theme.primaryColor)} />
        <Text style={[styles.title, { color: theme.textColor, fontFamily }]}>{data.title}</Text>
      </View>

      <Text style={[styles.timezone, { color: theme.mutedTextColor, fontFamily }]}>Timezone: {data.timezone}</Text>

      <View style={styles.section}>
        <Text style={[styles.sectionLabel, styles.sectionLabelSpacing, { color: theme.mutedTextColor, fontFamily }]}>Select Date</Text>
        <View style={styles.dateRow}>
          {groupedSlots.map((group) => {
            const isSelected = selectedDate === group.date;

            return (
              <Touchable
                key={group.date}
                hitSlop={DATE_HIT_SLOP}
                onPress={() => {
                  setSelectedDate(group.date);
                  setSelectedSlot(null);
                }}
              >
                <GradientFill
                  color={isSelected ? theme.primaryColor : theme.backgroundColor}
                  style={[styles.dateChip, { borderRadius: buttonRadius }, isSelected ? styles.selectedShadow : { borderWidth: 1, borderColor: theme.borderColor }]}
                >
                  <Text style={[styles.dateChipLabel, { color: isSelected ? theme.primaryTextColor : theme.textColor, fontFamily }]}>{group.dateLabel}</Text>
                </GradientFill>
              </Touchable>
            );
          })}
        </View>
      </View>

      {selectedDateGroup ? (
        <Animated.View style={[styles.section, { opacity: slotFade }]}>
          <View style={styles.sectionLabelRow}>
            <Clock size={12} color={theme.mutedTextColor} />
            <Text style={[styles.sectionLabel, { color: theme.mutedTextColor, fontFamily }]}>Select Time</Text>
          </View>
          <View style={styles.slotGrid}>
            {selectedDateGroup.slots.map((slot) => {
              const isSelected = selectedSlot?.id === slot.id;

              return (
                <View key={slot.id} style={styles.slotCell}>
                  <Touchable hitSlop={SLOT_HIT_SLOP} onPress={() => setSelectedSlot(slot)}>
                    <GradientFill
                      color={isSelected ? theme.primaryColor : theme.backgroundColor}
                      style={[styles.slot, { borderRadius: buttonRadius, borderColor: isSelected ? solidColor(theme.primaryColor) : theme.borderColor }, isSelected ? styles.selectedShadow : null]}
                    >
                      <Text style={[styles.slotLabel, { color: isSelected ? theme.primaryTextColor : theme.textColor, fontFamily }]} numberOfLines={1}>
                        {formatSlotTime(slot.datetime, data.timezone)}
                      </Text>
                    </GradientFill>
                  </Touchable>
                </View>
              );
            })}
          </View>
        </Animated.View>
      ) : null}

      <Touchable onPress={handleConfirm} disabled={isConfirmDisabled}>
        <GradientFill color={isConfirmDisabled ? theme.borderColor : theme.primaryColor} style={[styles.confirmButton, { borderRadius: buttonRadius }]}>
          {isProcessing ? (
            <>
              <Spinner color={confirmTextColor} />
              <Text style={[styles.confirmLabel, { color: confirmTextColor, fontFamily }]}>Processing...</Text>
            </>
          ) : (
            <Text style={[styles.confirmLabel, { color: confirmTextColor, fontFamily }]}>Next</Text>
          )}
        </GradientFill>
      </Touchable>
    </View>
  );
};

const styles = StyleSheet.create({
  header: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 12 },
  title: { fontSize: 14, lineHeight: 20, fontWeight: "500" },
  timezone: { fontSize: 12, lineHeight: 16, marginBottom: 12 },
  section: { marginBottom: 12 },
  sectionLabel: { fontSize: 12, lineHeight: 16, fontWeight: "600", textTransform: "uppercase", letterSpacing: 0.3 },
  sectionLabelSpacing: { marginBottom: 8 },
  sectionLabelRow: { flexDirection: "row", alignItems: "center", gap: 4, marginBottom: 8 },
  dateRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  dateChip: { paddingHorizontal: 12, paddingVertical: 8, alignItems: "center", justifyContent: "center" },
  dateChipLabel: { fontSize: 12, lineHeight: 16, fontWeight: "600" },
  // Web's `0 2px 4px rgba(0,0,0,0.1)` on the chosen chip, in both platforms' terms.
  selectedShadow: { shadowColor: "#000000", shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  // Three equal columns with `SLOT_GUTTER` between them: each cell pads half a
  // gutter per side and the row pulls that padding back off its outer edges.
  slotGrid: { flexDirection: "row", flexWrap: "wrap", rowGap: SLOT_GUTTER, marginHorizontal: -SLOT_GUTTER / 2 },
  slotCell: { width: "33.3333%", paddingHorizontal: SLOT_GUTTER / 2 },
  slot: { paddingHorizontal: 8, paddingVertical: 8, alignItems: "center", justifyContent: "center", borderWidth: 1 },
  slotLabel: { fontSize: 12, lineHeight: 16, fontWeight: "500" },
  confirmButton: { height: 36, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8 },
  confirmLabel: { fontSize: 14, lineHeight: 20, fontWeight: "600" },
});

export default CalendarStep;
